import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import api, { setInMemoryToken, clearInMemoryToken, getInMemoryToken } from '../utils/api';

// Plain axios instance used exclusively for the initial /auth/me check.
// It intentionally has NO 401-redirect response interceptor so that an
// unauthenticated state on page load does not trigger window.location.href
// changes that cause an endless reload loop on the login page.
const _authCheckApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});
_authCheckApi.interceptors.request.use(
  (config) => {
    const token = getInMemoryToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Token is stored in memory only (set as httpOnly cookie by backend — not in localStorage)
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    }
    // Restore in-memory token from sessionStorage so the Bearer header works on reload
    const savedToken = getInMemoryToken();
    if (savedToken) setToken(savedToken);
    // Always verify session with the server — role comes from server, never from storage
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      // Use _authCheckApi (no 401-redirect interceptor) so that an unauthenticated
      // response does not trigger window.location.href and cause a reload loop.
      const response = await _authCheckApi.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      // 401 or any network error — clear local state and stay on the current page.
      clearInMemoryToken();
      setUser(null);
      setToken(null);
    } finally {
      // Resolve the loading gate so the app renders (login page or dashboard).
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;

    // Keep token in memory for Bearer fallback (cookie is set by server as httpOnly)
    setToken(access_token);
    setInMemoryToken(access_token);
    // Always use server-returned user data — do not cache role in localStorage
    setUser(userData);

    // Signal BrandingContext to load org branding now that a token is available
    window.dispatchEvent(new CustomEvent('auth:login'));

    const savedTheme = userData.theme_preference || 'light';
    setTheme(savedTheme);
    localStorage.setItem('theme', savedTheme);
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    return userData;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (_) {
      // Ignore errors — always clear local state
    }
    setToken(null);
    clearInMemoryToken();
    setUser(null);
  };

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    if (user) {
      try {
        await api.put(`/auth/theme?theme=${newTheme}`);
      } catch (error) {
        console.error('Error updating theme:', error);
      }
    }
  };

  const updateUser = (updatedData) => {
    setUser((prev) => prev ? { ...prev, ...updatedData } : prev);
  };

  const value = {
    user,
    token,
    loading,
    theme,
    login,
    logout,
    toggleTheme,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
